import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import myAccountStore, { MyAccountStore } from './my-account-store';
import { getMyGroups } from './api';

const isLoading = ref(false);
let runningRequest: Promise<void> | null = null;

const useMyGroups = (loadImmediately = false) => {
  const store = myAccountStore();
  const { groups } = storeToRefs(store) as unknown as MyAccountStore;
  const { reset, setGroups, invalidateGroups } = store;
  const isFetchingGroups = ref<boolean>(false);

  const shouldRefreshData = (): boolean => {
    const ttl = 2 * 60 * 60 * 1000; // 2 hours
    return Date.now() - groups.value.updatedAt > ttl;
  };

  /**
   *
   * Load all user groups and save to the persistent store if the data doesn't exist or older than constraints (2 hours).
   * "isLoading" ref is for tracing the loading.
   * "groups" where the data will be held.
   */
  const loadMyGroups = async ({ force = false }: { force?: boolean } = {}): Promise<void> => {
    if (runningRequest) {
      return runningRequest;
    }

    if (!force && !shouldRefreshData()) {
      return Promise.resolve();
    }

    isLoading.value = true;
    runningRequest = (async () => {
      try {
        const chunkSize = 100;
        const { data, meta } = await getMyGroups(chunkSize, 0);

        if (data?.length === 0 || meta?.totalCount === 0) {
          setGroups({ data: [], meta: { totalCount: 0 }, updatedAt: Date.now() });
          return;
        }

        const leftGroups = meta.totalCount - (data.length + 1);

        if (leftGroups > 0) {
          const count = Math.ceil(leftGroups / chunkSize);
          const calls = Array.from({ length: count }).map((_, i) => getMyGroups(100, (i + 1) * chunkSize));
          const responses = await Promise.all(calls);
          data.push(...responses.flatMap((r) => r.data));
        }

        setGroups({ data, meta, updatedAt: Date.now() });
      } finally {
        isLoading.value = false;
        runningRequest = null;
      }
    })();

    return runningRequest;
  };

  if (loadImmediately && !isLoading.value) {
    loadMyGroups();
  }

  /**
   * direct access to API.
   * "isFetchingGroups" ref is used for tracking fetching
   * No error handling done.
   */
  const fetchMyGroups = (limit: number, offset: number) => {
    isFetchingGroups.value = true;
    return getMyGroups(limit, offset).finally(() => {
      isFetchingGroups.value = false;
    });
  };

  return {
    fetchMyGroups,
    isLoading,
    loadMyGroups,
    groups,
    invalidateGroups,
    reset,
  };
};

export default useMyGroups;
